import type { Observation, ObservationComponent } from '@medplum/fhirtypes';

/**
 * Code-driven observation presentations: the vital-specific knowledge lives
 * here as data, never in view code. Matching is by LOINC code first (the
 * reliable signal in the wild) with HL7 profile canonicals as a secondary
 * key for stamped data. Adding a vital = adding a table row.
 *
 * Pure module on purpose — no React, no registry coupling — so any consumer
 * (views, table cells, chips, other renderers) can share it.
 */

const LOINC_SYSTEM = 'http://loinc.org';
const HL7_PROFILE_BASE = 'http://hl7.org/fhir/StructureDefinition';

export type ObservationPresentation = {
  id: string;
  /** LOINC codes this presentation claims (panel root code for panels). */
  codes: string[];
  /** Canonical profile URLs, matched against `meta.profile` when present. */
  profiles?: string[];
  /**
   * Lowercased `code.text` values this presentation claims — the degradation
   * key for real-world data that carries display text but no coding. Codes
   * and profiles always win; text only matches when the observation has no
   * recognizable LOINC coding at all.
   */
  textAliases?: string[];
  /** Display title override — canonical label instead of source code text. */
  title?: string;
  /** Panel shape: ordered per-component extraction by LOINC. */
  components?: { code: string; label: string }[];
  /** How composed panel values read: `slash` → "128/82 mmHg". */
  compose?: 'slash' | 'list';
};

/** The HL7 vital-signs value set — official LOINC codes and profiles only. */
export const VITAL_SIGNS_PRESENTATIONS: ObservationPresentation[] = [
  {
    id: 'blood-pressure-panel',
    codes: ['85354-9'],
    profiles: [`${HL7_PROFILE_BASE}/bp`],
    title: 'Blood pressure',
    textAliases: ['blood pressure', 'blood pressure panel'],
    components: [
      { code: '8480-6', label: 'Systolic' },
      { code: '8462-4', label: 'Diastolic' },
    ],
    compose: 'slash',
  },
  {
    id: 'heart-rate',
    codes: ['8867-4'],
    profiles: [`${HL7_PROFILE_BASE}/heartrate`],
    title: 'Heart rate',
    textAliases: ['heart rate', 'pulse', 'pulse rate'],
  },
  {
    id: 'respiratory-rate',
    codes: ['9279-1'],
    profiles: [`${HL7_PROFILE_BASE}/resprate`],
    title: 'Respiratory rate',
    textAliases: ['respiratory rate'],
  },
  {
    id: 'body-temperature',
    codes: ['8310-5'],
    profiles: [`${HL7_PROFILE_BASE}/bodytemp`],
    title: 'Body temperature',
    textAliases: ['body temperature', 'temperature'],
  },
  {
    id: 'body-weight',
    codes: ['29463-7'],
    profiles: [`${HL7_PROFILE_BASE}/bodyweight`],
    title: 'Body weight',
    textAliases: ['body weight', 'weight'],
  },
  {
    id: 'body-height',
    codes: ['8302-2'],
    profiles: [`${HL7_PROFILE_BASE}/bodyheight`],
    title: 'Body height',
    textAliases: ['body height', 'height'],
  },
  {
    id: 'oxygen-saturation',
    codes: ['59408-5', '2708-6'],
    profiles: [`${HL7_PROFILE_BASE}/oxygensat`],
    title: 'Oxygen saturation',
    textAliases: ['oxygen saturation', 'spo2'],
  },
  {
    id: 'body-mass-index',
    codes: ['39156-5'],
    title: 'Body mass index',
    textAliases: ['body mass index', 'bmi'],
  },
];

/**
 * Find the presentation an observation matches, if any. Codes without a
 * system are accepted as LOINC — real-world payloads frequently omit it.
 */
export function resolveObservationPresentation(
  observation: Observation,
  presentations: ObservationPresentation[] = VITAL_SIGNS_PRESENTATIONS,
): ObservationPresentation | undefined {
  const codes = new Set(
    (observation.code?.coding ?? [])
      .filter((coding) => !coding.system || coding.system === LOINC_SYSTEM)
      .map((coding) => coding.code)
      .filter(Boolean),
  );
  const profiles = new Set(observation.meta?.profile ?? []);
  const matched = presentations.find(
    (presentation) =>
      presentation.codes.some((code) => codes.has(code)) ||
      (presentation.profiles ?? []).some((profile) => profiles.has(profile)),
  );
  if (matched) return matched;
  // Text fallback only when no LOINC coding exists to disagree with — a
  // coded observation that didn't match above stays unmatched rather than
  // being re-guessed from display text.
  if (codes.size > 0) return undefined;
  const text = observation.code?.text?.toLowerCase().trim();
  if (!text) return undefined;
  return presentations.find((presentation) =>
    (presentation.textAliases ?? []).includes(text),
  );
}

export type PanelComponentEntry = {
  label: string;
  component: ObservationComponent;
};

/**
 * The panel's components in presentation order, paired with their labels.
 * Components the observation doesn't carry are skipped — callers decide
 * whether a partial panel is renderable (composed values refuse it; a
 * per-component breakdown shows what exists).
 */
export function panelComponents(
  observation: Observation,
  presentation: ObservationPresentation,
): PanelComponentEntry[] {
  return (presentation.components ?? []).flatMap((spec) => {
    const component = observation.component?.find((candidate) =>
      candidate.code?.coding?.some(
        (coding) =>
          coding.code === spec.code &&
          (!coding.system || coding.system === LOINC_SYSTEM),
      ),
    );
    return component ? [{ label: spec.label, component }] : [];
  });
}

/**
 * Composed value for a panel presentation ("128/82 mmHg"). Undefined when
 * the presentation has no components or any component value is missing —
 * callers fall back to the default value rendering, never a half-panel.
 */
export function composedObservationValue(
  observation: Observation,
  presentation: ObservationPresentation,
): string | undefined {
  if (!presentation.components || presentation.components.length === 0) {
    return undefined;
  }
  const entries = panelComponents(observation, presentation);
  if (entries.length !== presentation.components.length) return undefined;
  const quantities = entries.map((entry) => entry.component.valueQuantity);
  if (quantities.some((quantity) => quantity?.value === undefined)) {
    return undefined;
  }
  const values = quantities.map((quantity) => String(quantity?.value));
  const unit = quantities[0]?.unit;
  if (presentation.compose === 'slash') {
    return `${values.join('/')}${unit ? ` ${unit}` : ''}`;
  }
  return entries
    .map((entry, index) => `${entry.label} ${values[index]}`)
    .join(', ');
}
